// ════════════════════════════════════════════════════════════════════
// PLAN ADVISORS COMMISSION STATEMENT PARSER
//
// Plan Advisors is a HEALTH-side aggregator FMO. They sell across
// many carriers — Molina, Centene/Ambetter, Oscar, Aetna CVS, etc. —
// and deliver one CONSOLIDATED CSV every period covering whatever
// carriers had pay activity. This parser handles their format.
//
// VERIFIED LAYOUT (against the Jun 8 2026 statement)
// ──────────────────────────────────────────────────────────────────
// Row 1: HEADER. 26 named columns:
//   Generated From, Payment Date, Agent ID, Payee ID, Payee Name,
//   Payee NPN, Statement Date, Carrier, Product Subcategory,
//   Product, Policy, Insured, Age, Effective Date, Payout Type,
//   Writing Agent Name, Writing Agent Number, Transaction Type,
//   New To Medicare, Carrier Transaction Type,
//   Additional Carrier Transaction Type, Member Count,
//   Original Eff Date, Policy Term Date, Commission Month, Amount
// Rows 2..N: DATA rows. One row per MEMBER per period. Households
//   share a Policy (multiple members at TX-0019528873 = one family).
// Rows N+1..end: FOOTER. Empty first column + a label string in
//   the second-to-last column (e.g. "Total Amount:") and a $-prefixed
//   value in the last column. We use these to cross-check the sum
//   of detail-row Amount values.
//
// COMMISSION SHAPE
// ──────────────────────────────────────────────────────────────────
// · ACA Marketplace pays PMPM. Molina IFP runs $25/member/month in
//   Texas; other carriers vary. The Amount column = the PMPM rate
//   for THIS member for THIS month. Sum across members = the
//   household's monthly check.
// · Payout Type signals lifecycle:
//     · "New Business"  — first month
//     · "Renewal"       — recurring (most rows)
//     · "Override"      — agency override on a downline write
//     · "Chargeback"    — negative (refund)
// · Policy Term Date populated = the member's coverage ENDED. This
//   is the disenrollment signal. Flag in the anomaly drawer so the
//   agent sees who fell off the book this period.
//
// FIELD MAPPING (Plan Advisors → health_policies / clients)
// ──────────────────────────────────────────────────────────────────
//   Insured (LAST, FIRST)     → client_name (titlecased FIRST LAST)
//   Policy                    → member_id (carrier's policy/member id)
//   Carrier                   → carrier
//   Product Subcategory       → plan_type (mapped via PRODUCT_MAP)
//   Amount                    → monthly_commission (period rate)
//   Payment Date              → last_commission_at
//   Effective Date            → effective_date
//   Policy Term Date          → termination_date (when present)
//   Writing Agent Number      → agent_prod_num
//
// NOT in Plan Advisors export (left null):
//   client_phone, client_email, client_dob, client_address.
//   These come from Jarvis or HealthSherpa exports.
//
// MULTI-CARRIER NOTE
// ──────────────────────────────────────────────────────────────────
// Plan Advisors can deliver Molina + Centene + Oscar rows in the
// same file. The parser groups by (Carrier, Policy) for the summary
// chips so the agent sees per-carrier totals at a glance.
// ════════════════════════════════════════════════════════════════════

const PLAN_ADVISORS_HEADER_SIGNATURE = [
  'Generated From', 'Payment Date', 'Payee NPN', 'Carrier', 'Insured', 'Policy',
];

const PRODUCT_MAP = {
  'AFFORDABLE CARE ACT':  'aca_marketplace',
  'ACA MARKETPLACE':      'aca_marketplace',
  'MEDICARE ADVANTAGE':   'medicare_advantage',
  'MEDICARE ADVANTAGE PRESCRIPTION DRUG': 'medicare_advantage',
  'MEDICARE SUPPLEMENT':  'medicare_supplement',
  'MEDIGAP':              'medicare_supplement',
  'MEDICARE PART D':      'medicare_pdp',
  'PRESCRIPTION DRUG':    'medicare_pdp',
  'PDP':                  'medicare_pdp',
  'DENTAL':               'dental_vision',
  'DENTAL/VISION':        'dental_vision',
  'VISION':               'dental_vision',
  'HOSPITAL INDEMNITY':   'hospital_indemnity',
  'SHORT-TERM MEDICAL':   'short_term',
  'STM':                  'short_term',
};

// Status mapping based on payout type + termination date.
function mapStatus({ payoutType, policyTermDate, transactionType }) {
  if (transactionType && /chargeback|refund/i.test(transactionType)) return 'cancelled';
  if (policyTermDate) return 'cancelled';
  return 'active';
}

// Quick CSV line splitter (handles quoted fields with embedded
// commas — "ROGERS, LAKIA" stays as one cell).
function parseCsvLine(line) {
  const out = [];
  let cur = '';
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQ) {
      if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; }
      else if (ch === '"') inQ = false;
      else cur += ch;
    } else {
      if (ch === ',') { out.push(cur); cur = ''; }
      else if (ch === '"') inQ = true;
      else cur += ch;
    }
  }
  out.push(cur);
  return out.map(s => s.trim());
}

// Detection — banner-free, so we match the header row signature.
// The header carries the 6-token combo above that doesn't co-occur
// in any other carrier statement we see.
export function isPlanAdvisorsFormat(text) {
  const head = String(text || '').split(/\r?\n/, 8).join('\n');
  return PLAN_ADVISORS_HEADER_SIGNATURE.every(s => head.includes(s));
}

// "ROGERS, LAKIA" → "Lakia Rogers" (titlecase).
function flipNameLastFirst(s) {
  const str = String(s || '').trim();
  if (!str) return '';
  if (str.includes(',')) {
    const [last, first] = str.split(',').map(t => t.trim());
    return titleCase(`${first} ${last}`.trim());
  }
  return titleCase(str);
}

function titleCase(s) {
  return String(s || '')
    .toLowerCase()
    .replace(/\b([a-z])/g, (_, c) => c.toUpperCase());
}

// MM/DD/YYYY → ISO YYYY-MM-DD. Returns null on bad input.
function parseDate(mmddyyyy) {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(String(mmddyyyy || '').trim());
  if (!m) return null;
  const mm = String(m[1]).padStart(2, '0');
  const dd = String(m[2]).padStart(2, '0');
  return `${m[3]}-${mm}-${dd}`;
}

function parseMoney(s) {
  if (s == null) return 0;
  const n = Number(String(s).replace(/[$,\s]/g, ''));
  return Number.isFinite(n) ? n : 0;
}

// Parse the entire statement. Returns:
//   { rows, agent, footer, summary }
//
// `rows` is the array of detail rows in the shape our existing
// import planner consumes — same field names as the generic-CSV
// path (`client_name`, `carrier`, `plan_type`, `member_id`,
// `monthly_commission`, `effective_date`, `termination_date`,
// `status`, `lead_source`).
export function parsePlanAdvisorsStatement(text) {
  const lines = String(text || '').split(/\r?\n/);
  let headerIdx = -1;
  for (let i = 0; i < lines.length; i++) {
    if (PLAN_ADVISORS_HEADER_SIGNATURE.every(s => lines[i].includes(s))) {
      headerIdx = i; break;
    }
  }
  if (headerIdx < 0) return emptyResult();

  const header = parseCsvLine(lines[headerIdx]);
  const col = {};
  header.forEach((h, i) => { col[h.trim()] = i; });
  const idx = (...aliases) => {
    for (const a of aliases) {
      const k = Object.keys(col).find(c => c.toLowerCase() === a.toLowerCase());
      if (k != null) return col[k];
    }
    return -1;
  };
  const C = {
    paymentDate:         idx('Payment Date'),
    payeeName:           idx('Payee Name'),
    payeeNpn:            idx('Payee NPN'),
    statementDate:       idx('Statement Date'),
    carrier:             idx('Carrier'),
    productSubcategory:  idx('Product Subcategory'),
    product:             idx('Product'),
    policy:              idx('Policy'),
    insured:             idx('Insured'),
    age:                 idx('Age'),
    effectiveDate:       idx('Effective Date'),
    payoutType:          idx('Payout Type'),
    writingAgentName:    idx('Writing Agent Name'),
    writingAgentNumber:  idx('Writing Agent Number'),
    transactionType:     idx('Transaction Type'),
    newToMedicare:       idx('New To Medicare'),
    memberCount:         idx('Member Count'),
    origEffDate:         idx('Original Eff Date'),
    policyTermDate:      idx('Policy Term Date'),
    commissionMonth:     idx('Commission Month'),
    amount:              idx('Amount'),
  };

  const rows = [];
  let agentMeta = null;
  const footer = { totalAmount: null, paymentAmount: null };

  for (let i = headerIdx + 1; i < lines.length; i++) {
    const raw = lines[i];
    if (!raw || !raw.trim()) continue;
    const cells = parseCsvLine(raw);
    const get = (k) => (C[k] >= 0 ? (cells[C[k]] || '').trim() : '');

    // Footer detection — the rows that read "Total Amount: / $150.00"
    // have empty leading cells but a label in the second-to-last cell
    // and a $-value in the last cell.
    const isFooter = !get('paymentDate') && !get('carrier') && !get('insured')
      && cells.some(c => /^Total Amount|^Payment Amount/i.test(c));
    if (isFooter) {
      const labelIdx = cells.findIndex(c => /^Total Amount|^Payment Amount/i.test(c));
      if (labelIdx >= 0) {
        const label = cells[labelIdx];
        const val = parseMoney(cells[labelIdx + 1]);
        if (/^Total Amount/i.test(label))   footer.totalAmount   = val;
        if (/^Payment Amount/i.test(label)) footer.paymentAmount = val;
      }
      continue;
    }

    const insured = get('insured');
    const carrier = get('carrier');
    if (!insured || !carrier) continue;

    // Capture agent meta once.
    if (!agentMeta && get('payeeName')) {
      agentMeta = {
        payeeName: get('payeeName'),
        payeeNpn:  get('payeeNpn'),
        writingAgentName:   get('writingAgentName'),
        writingAgentNumber: get('writingAgentNumber'),
        paymentDate: parseDate(get('paymentDate')),
        statementDate: parseDate(get('statementDate')),
      };
    }

    const productSub  = get('productSubcategory').toUpperCase();
    const planType    = PRODUCT_MAP[productSub] || 'other';
    const policyTermDate = parseDate(get('policyTermDate'));

    rows.push({
      // ── source fields (kept for audit + UI preview) ──
      _source: 'plan_advisors',
      _payoutType:      get('payoutType'),
      _transactionType: get('transactionType'),
      _productSubcategory: productSub,
      _newToMedicare:   get('newToMedicare'),
      _memberCount:     Number(get('memberCount')) || 1,
      _commissionMonth: get('commissionMonth'),
      _paymentDate:     parseDate(get('paymentDate')),

      // ── health_policies upsert payload (matches generic-CSV path) ──
      client_name:        flipNameLastFirst(insured),
      client_phone:       null,
      client_email:       null,
      client_dob:         null,
      carrier:            carrier,
      plan_name:          get('product') || productSub || planType,
      plan_type:          planType,
      member_id:          get('policy'),
      monthly_premium:    null,        // not in this export
      monthly_commission: parseMoney(get('amount')),
      subsidy_amount:     null,
      effective_date:     parseDate(get('effectiveDate')) || parseDate(get('origEffDate')),
      renewal_date:       null,
      termination_date:   policyTermDate,
      last_commission_at: parseDate(get('paymentDate')),
      status:             mapStatus({
        payoutType:     get('payoutType'),
        policyTermDate, transactionType: get('transactionType'),
      }),
      lead_source:        'Plan Advisors',
      agent_prod_num:     get('writingAgentNumber') || null,
      notes:              null,
    });
  }

  // Summary roll-up — drives the preview chips.
  const summary = rows.reduce((acc, r) => {
    acc.totalRows++;
    acc.totalAmount += Number(r.monthly_commission) || 0;
    acc.byCarrier[r.carrier]   = (acc.byCarrier[r.carrier]   || 0) + 1;
    acc.byPlanType[r.plan_type] = (acc.byPlanType[r.plan_type] || 0) + 1;
    acc.byPayoutType[r._payoutType] = (acc.byPayoutType[r._payoutType] || 0) + 1;
    if (r.termination_date) acc.terminations++;
    // Households — multiple rows sharing (carrier, member_id).
    const key = `${r.carrier}::${r.member_id}`;
    acc.byPolicy[key] = (acc.byPolicy[key] || 0) + 1;
    return acc;
  }, {
    totalRows: 0, totalAmount: 0, terminations: 0,
    byCarrier: {}, byPlanType: {}, byPayoutType: {}, byPolicy: {},
  });
  summary.householdCount = Object.values(summary.byPolicy).filter(n => n > 1).length;
  summary.uniquePolicies = Object.keys(summary.byPolicy).length;

  // Footer reconciliation — the agent should see if the file total
  // doesn't match the sum of detail rows (corrupt download, missing
  // last page, etc.).
  summary.footerMismatch = footer.totalAmount != null
    && Math.abs(footer.totalAmount - summary.totalAmount) > 0.01;

  return { rows, agent: agentMeta, footer, summary };
}

function emptyResult() {
  return {
    rows: [], agent: null,
    footer: { totalAmount: null, paymentAmount: null },
    summary: {
      totalRows: 0, totalAmount: 0, terminations: 0,
      byCarrier: {}, byPlanType: {}, byPayoutType: {}, byPolicy: {},
      householdCount: 0, uniquePolicies: 0, footerMismatch: false,
    },
  };
}
